/**
 * Journey: Egg Freezing, Solo, Female
 *
 * The most common entry point. Steps: Learn > Test > Clinic > Prep > Treatment
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

export const EGG_FREEZING_GUIDE_ARTICLE_ID =
  "moon-article-egg-freezing-timeline";

const learnStep: PlanStep = {
  id: "learn",
  title: "How Egg Freezing Works",
  subtitle: "Understand the process before you begin",
  icon: "BookOpen",
  education: {
    context:
      "Your ovaries contain a limited number of eggs that decline in quantity and quality over time. Egg freezing captures eggs at their current quality by stimulating your ovaries to mature multiple eggs at once, retrieving them, and flash-freezing (vitrifying) them for future use.",
    details:
      'The process takes about 2 weeks of active treatment:\n• You\'ll take injectable medications (gonadotropins) for 10-14 days to stimulate your ovaries\n• Every 2-3 days, your clinic monitors with blood work (estradiol, LH) and ultrasound (follicle count and size)\n• When follicles are ready, a "trigger shot" matures the eggs for retrieval\n• Egg retrieval is a 20-minute procedure under light sedation — a thin needle guided by ultrasound aspirates fluid from each follicle\n• Retrieved eggs are evaluated for maturity, then vitrified (flash-frozen at -196°C) and stored in liquid nitrogen',
    learnMoreLink: EGG_FREEZING_GUIDE_ARTICLE_ID,
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
      params: { stepId: "learn" },
    },
    {
      label: "Step-by-step visual guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "ArticleDetail",
      params: { articleId: EGG_FREEZING_GUIDE_ARTICLE_ID },
    },
  ],
  completionSignal: { type: "manual" },
  ageSpecificContent: [
    {
      range: [0, 34],
      content:
        "At your age, most women retrieve 10-20 eggs per cycle. One cycle often provides a good foundation.",
    },
    {
      range: [35, 37],
      content:
        "At your age, egg quality begins declining more noticeably. Most women retrieve 8-14 eggs. Many choose to do 2 cycles to increase their chances.",
    },
    {
      range: [38, 40],
      content:
        "At your age, both egg quantity and quality are declining. Expect 5-10 eggs per retrieval. Multiple cycles are commonly recommended.",
    },
    {
      range: [41, 99],
      content:
        "After 40, fewer eggs are typically retrieved and a higher proportion may have chromosomal abnormalities. Your clinic will discuss realistic expectations.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Baseline Fertility Testing",
  subtitle: "Understand your ovarian reserve and likely response",
  icon: "TestTube",
  education: {
    context:
      "Baseline testing helps you understand your ovarian reserve — how many eggs may be available and how your body may respond to stimulation. Recent results can also help a clinic personalize your plan, but the first goal is clarity for you.",
  },
  actions: [
    {
      label: "Check out the tests",
      variant: "primary",
      icon: "ShoppingBag",
      screen: "AtHomeFertilityTesting",
    },
    {
      label: "Find a lab near you",
      variant: "secondary",
      icon: "MapPin",
      screen: "FindCareScreen",
    },
    {
      label: "Upload test results",
      variant: "outline",
      icon: "Upload",
      signal: "open-document-upload",
      params: { initialCategory: "medical_records" },
    },
  ],
  substeps: [
    {
      id: "amh",
      title: "AMH (Anti-Müllerian Hormone)",
      education:
        "Measures your egg supply — the single most important test for protocol planning. A simple blood test that can be done at home or at any lab, any day of your cycle.",
      status: "not-started",
      actions: [
        {
          label: "Order at-home test",
          variant: "primary",
          screen: "AtHomeFertilityTesting",
        },
        { label: "Find a lab", variant: "outline", screen: "FindCareScreen" },
      ],
    },
    {
      id: "fsh-estradiol",
      title: "FSH + Estradiol",
      education:
        "Must be done on cycle day 2-3. FSH (follicle-stimulating hormone) shows how hard your body is working to recruit eggs. High FSH can indicate lower reserve. Estradiol provides context for the FSH reading.",
      status: "not-started",
      actions: [],
    },
    {
      id: "afc",
      title: "Antral Follicle Count (AFC)",
      education:
        "Transvaginal ultrasound that counts the small follicles visible at the start of your cycle. Combined with AMH, gives the most accurate picture of expected egg yield. This can be done at your fertility clinic, through a mobile testing service, or you may already have recent results to upload.",
      status: "not-started",
      actions: [
        {
          label: "Upload existing results",
          variant: "primary",
          icon: "Upload",
          signal: "open-document-upload",
          params: { initialCategory: "medical_records" },
        },
        {
          label: "Find a clinic or mobile panel",
          variant: "outline",
          screen: "FindCareScreen",
        },
      ],
    },
  ],
  completionSignal: { type: "test-result-exists", testIds: ["amh"] },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Choose where you'll do your cycle",
  icon: "Building2",
  education: {
    context:
      "Your fertility clinic manages your entire cycle — from planning your medication protocol to performing the egg retrieval. Choosing a clinic early lets them review your test results and give you a personalized timeline and cost estimate.",
    details:
      "Things that matter when choosing:\n• Success rates — SART reports clinic-specific data (though egg freezing rates are newer and less standardized than IVF)\n• Location — You'll visit 4-6 times during monitoring, often early morning. Proximity matters.\n• Cost — Prices vary significantly between clinics, even in the same city\n• Insurance — Not all clinics accept all plans for fertility treatment",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      icon: "Search",
      screen: "FindCareScreen",
      params: { filter: "egg-freezing" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      icon: "DollarSign",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education:
        "Look at success rates, reviews, location, and whether they accept your insurance.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education:
        "Most clinics offer a 30-60 minute initial consultation to review your case and discuss options.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education:
        "Once you've found the right fit, your clinic will start your onboarding process.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Complete paperwork and preparation",
  icon: "ClipboardCheck",
  education: {
    context:
      "Before treatment starts, there's paperwork and preparation your clinic needs. Completing this early means your cycle can start as soon as you're ready — no delays for missing documents.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      icon: "FileText",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      icon: "CreditCard",
      screen: "CoverageFlow",
    },
    {
      label: "Browse supplements",
      variant: "outline",
      icon: "Pill",
      screen: "SupplementsScreen",
      params: { goal: "egg-quality" },
    },
  ],
  substeps: [
    {
      id: "medical-history",
      title: "Complete medical history",
      education:
        "Your clinic uses this to identify anything that affects your protocol (allergies, medications, prior surgeries, conditions).",
      status: "not-started",
      actions: [
        {
          label: "Continue medical history",
          variant: "primary",
          screen: "MedicalHistory",
        },
      ],
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education:
        "Needed for billing, even if fertility isn't covered — diagnostics and anesthesia often are.",
      status: "not-started",
      actions: [
        {
          label: "Scan insurance card",
          variant: "primary",
          screen: "CoverageFlow",
        },
      ],
    },
    {
      id: "pre-auth",
      title: "Pre-authorization (if applicable)",
      education:
        "If your plan covers egg freezing, your clinic submits a pre-auth request. This can take 1-2 weeks.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Your clinic provides these. They cover the procedure, risks, and storage terms.",
      status: "not-started",
      actions: [
        {
          label: "Upload signed consents",
          variant: "outline",
          screen: "DocumentsScreen",
          params: { initialCategory: "legal_consent" },
        },
      ],
    },
    {
      id: "order-medications",
      title: "Order medications",
      education:
        "Stimulation medications (gonadotropins) cost $3,000-$6,000 and require a specialty pharmacy. Your clinic will send your prescription once your protocol is finalized. Compare pharmacy prices — they vary significantly.",
      status: "not-started",
      actions: [
        {
          label: "Compare pharmacy prices",
          variant: "primary",
          screen: "PharmacyScreen",
        },
      ],
    },
    {
      id: "supplements",
      title: "Start supplements (optional)",
      education:
        "CoQ10 and vitamin D are commonly recommended 2-3 months before a cycle to support egg quality.",
      status: "not-started",
      actions: [
        {
          label: "See recommended supplements",
          variant: "outline",
          screen: "SupplementsScreen",
          params: { goal: "egg-quality" },
        },
      ],
    },
  ],
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Start Your Cycle",
  subtitle: "Active treatment with daily guidance",
  icon: "Syringe",
  education: {
    context:
      "Your egg freezing cycle is active. Track daily medications, appointments, and symptoms in the Care section.",
    details:
      "Phases: baseline → stimulation (10-14 days of daily injections) → trigger shot → retrieval (~20 min under light sedation).\n\nYour eggs are then vitrified (flash-frozen) and stored.\n\nRecovery: 1-2 days. Most return to normal activities within 24-48 hours.\n\nYour Care page has your daily medication schedule, symptom tracker, and appointment calendar.",
  },
  ageSpecificContent: [
    {
      range: [0, 34],
      content:
        "At your age, your first cycle often yields strong results. Your clinic will design a protocol optimized for your ovarian reserve.",
    },
    {
      range: [35, 37],
      content:
        "At your age, a well-designed protocol is important. Your clinic may be more aggressive with dosing to maximize egg yield.",
    },
    {
      range: [38, 99],
      content:
        "At your age, your clinic will carefully optimize your protocol. Multiple cycles may be recommended — each cycle provides data to refine the approach.",
    },
  ],
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    {
      label: "Set your start date",
      variant: "outline",
      icon: "Calendar",
      signal: "open-start-treatment-modal",
      params: { presetCycleType: "egg_freezing" },
    },
  ],
  completionSignal: { type: "external" },
};

const storageStep: PlanStep = {
  id: "storage",
  title: "Your Frozen Eggs",
  subtitle: "Manage storage and plan next steps",
  icon: "Snowflake",
  education: {
    context:
      "Your eggs are vitrified (flash-frozen) and stored in liquid nitrogen tanks at your clinic or a partner cryo facility. They remain viable for decades. Most facilities charge $500-$800/year for storage.",
    details:
      "Things to keep in mind:\n• Annual storage fees are typically billed each year on the anniversary of your retrieval\n• You can transfer eggs between facilities if you move — ask your clinic about the process\n• Some employers and insurers now cover storage fees — check your benefits\n• If you decide to use your eggs, they are thawed, fertilized via ICSI, and the resulting embryos are transferred",
  },
  actions: [
    {
      label: "View your cryo vault",
      variant: "primary",
      icon: "Snowflake",
      screen: "CryoVaultScreen",
    },
    {
      label: "Start another cycle",
      variant: "outline",
      icon: "Plus",
      signal: "open-start-treatment-modal",
      params: { presetCycleType: "egg_freezing" },
    },
  ],
  substeps: [
    {
      id: "confirm-count",
      title: "Confirm eggs in storage",
      education:
        "After retrieval, your clinic will tell you how many eggs were mature and successfully vitrified. This may be fewer than the number retrieved — that's normal.",
      status: "not-started",
      actions: [
        {
          label: "View cryo vault",
          variant: "primary",
          screen: "CryoVaultScreen",
        },
      ],
    },
    {
      id: "storage-payment",
      title: "Set up storage payments",
      education:
        "Most clinics bill annually. Some offer multi-year discounts. Ask your clinic about payment options and whether your insurance covers any portion.",
      status: "not-started",
    },
    {
      id: "future-plan",
      title: "Think about next steps",
      education:
        "There's no rush. Some women freeze more eggs in additional cycles to increase their bank. Others store and revisit when they're ready to use them.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

export const EGG_FREEZING_SOLO: JourneyDefinition = {
  key: "egg-freezing-solo",
  gender: "female",
  goal: "preserve",
  pathway: "solo",
  title: "Egg Freezing",
  description: "Preserve your eggs for future use",
  steps: [
    learnStep,
    testStep,
    clinicStep,
    prepStep,
    treatmentStep,
    storageStep,
  ],
};
